// Offer > Reference Check (lane B8 S13): Step 18 on the design system's ReferenceCheck. The Recruiter manages it
// (requirement flag, owner, provider, requested and completed dates, outcome, notes); authorized HR roles view it;
// interviewers and candidates see nothing. Not Required is one line: his rule that it blocks nothing.
import { useState } from "react";
import { Card, COMMON_LABELS, ErrorStrip, Pill, ReferenceCheck, REFERENCE_STATUS, RULE_LABELS, Stack, type PersonOption, type SelectOption } from "@/mc";
import { referenceComplete, referenceOpen, referenceRecordRequested, referenceSetRequirement, type ReferenceCompleteInput, type ReferenceOpenOutput } from "@/api/tools";
import { businessDayISO } from "@/api/runtime";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import type { OfferTabProps } from "../types";
import { REF_HS } from "./hs";
import { REF_TAB_LABELS } from "./labels";

export type RefStatus = "notRequired" | "required" | "requested" | "inProgress" | "completed" | "unableToComplete";

/** reference.open's V2 shape (PARALLEL-MAP §4): V1 fields plus display_status and the requirement record. */
export type ReferenceOut = ReferenceOpenOutput & {
  display_status?: string | null;
  requirement?: {
    required?: boolean;
    owner?: string | null;
    due_at?: string | null;
    requested_at?: string | null;
    completed_at?: string | null;
    provider?: string | null;
    unable_at?: string | null;
    unable_reason?: string | null;
    source?: string | null;
  } | null;
  // REBASE: B8 S11 the configured choices served with the check (ReferenceProvider list, outcome list, owners).
  owners?: PersonOption[];
  providers?: SelectOption[];
  outcomes?: SelectOption[];
};

const DISPLAY: Record<string, RefStatus> = {
  not_required: "notRequired", notRequired: "notRequired", required: "required", requested: "requested",
  in_progress: "inProgress", inProgress: "inProgress", completed: "completed", unable_to_complete: "unableToComplete",
  unableToComplete: "unableToComplete",
};

/** The check's status: the server's display status, else derived from the V1 fields. */
export function refStatus(ref: ReferenceOut): RefStatus {
  const shown = ref.display_status ? DISPLAY[ref.display_status] : undefined;
  if (shown) return shown;
  if (ref.requirement?.unable_at) return "unableToComplete";
  if (ref.status === "complete" || ref.completed_at) return "completed";
  if (ref.status === "waived" || ref.status === "not_elected" || ref.requirement?.required === false) return "notRequired";
  if (ref.contacts.length > 0) return "inProgress";
  if (ref.requirement?.requested_at) return "requested";
  return "required";
}

const day = (at: string | null | undefined): string | undefined => (at ? at.slice(0, 10) : undefined);

/** The current value as its only choice when the configured list has not arrived. */
const withCurrent = (options: SelectOption[] | undefined, value: string | null | undefined): SelectOption[] => {
  const list = options ?? [];
  return value && !list.some((o) => o.value === value) ? [...list, { value, label: value }] : list;
};

export interface RefDraft { owner?: string | undefined; provider?: string | undefined; requested?: string | undefined; completed?: string | undefined; outcome?: string | undefined; notes?: string | undefined }

export interface ReferenceActions {
  setRequirement?: (required: boolean) => Promise<unknown>;
  record?: (status: RefStatus, draft: RefDraft) => Promise<unknown>;
}

export interface ReferenceViewProps {
  reference: ReferenceOut;
  roles: readonly string[];
  actions?: ReferenceActions | undefined;
  timeZone?: string | undefined;
  error?: string | undefined;
}

const HR_SEATS = ["hrbp", "hm", "ta_manager", "coordinator", "compensation", "admin"];

/** The pure view (the SSR test renders it per seat without a network). */
export function ReferenceView({ reference, roles, actions = {}, timeZone, error }: ReferenceViewProps) {
  const status = refStatus(reference);
  const manages = roles.includes("recruiter") && reference.can.record !== false && reference.can.manage !== false;
  const seat = manages ? "recruiter" : roles.some((r) => HR_SEATS.includes(r)) ? "hr" : "interviewer";
  const req = reference.requirement ?? {};
  const [draft, setDraft] = useState<RefDraft>({
    owner: req.owner ?? undefined, provider: req.provider ?? undefined, requested: day(req.requested_at),
    completed: day(req.completed_at ?? reference.completed_at), outcome: reference.outcome ?? undefined, notes: reference.notes ?? undefined,
  });
  const [busy, setBusy] = useState(false);
  if (seat === "interviewer") return null;
  if (status === "notRequired" && (seat === "hr" || !actions.setRequirement)) {
    return (
      <p className="mc-ref-one">
        <Pill>{REFERENCE_STATUS.notRequired}</Pill> {RULE_LABELS.noBlockWhenNotRequired}
      </p>
    );
  }
  const put = (key: keyof RefDraft) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const recordable = status === "required" || status === "requested" || status === "inProgress";
  const missing = status === "required" ? !draft.requested : !draft.outcome;
  const run = async (fn: () => Promise<unknown>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <Stack>
      {error ? <ErrorStrip message={error} /> : null}
      <ReferenceCheck
        status={status}
        seat={seat}
        owners={reference.owners ?? (draft.owner ? [{ value: draft.owner, name: draft.owner }] : [])}
        owner={draft.owner}
        onOwner={put("owner")}
        providers={withCurrent(reference.providers, draft.provider)}
        provider={draft.provider}
        onProvider={put("provider")}
        outcomes={withCurrent(reference.outcomes, draft.outcome)}
        outcome={draft.outcome}
        onOutcome={put("outcome")}
        requested={draft.requested}
        onRequested={put("requested")}
        completed={draft.completed}
        onCompleted={put("completed")}
        notes={draft.notes}
        onNotes={put("notes")}
        onRequirement={actions.setRequirement ? (required) => { void run(() => actions.setRequirement!(required)); } : undefined}
        onRecord={recordable && actions.record ? () => { void run(() => actions.record!(status, draft)); } : undefined}
        recording={busy}
        recordBlocked={recordable && missing ? { reason: COMMON_LABELS.required } : undefined}
        timeZone={timeZone}
        hs={REF_HS}
      />
      <p className="mc-hint">{seat === "hr" ? REF_TAB_LABELS.hrRolesView : REF_TAB_LABELS.recruiterManages} {RULE_LABELS.sensitiveReferenceData}</p>
    </Stack>
  );
}

/** The tab: reads the check, binds the writes, and renders ReferenceView. */
export function ReferenceTab({ requisitionId, applicationId, reload }: OfferTabProps) {
  const { me } = useSession();
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const ids = { requisition_id: requisitionId, application_id: applicationId };
  const key = ["reference.open", requisitionId, applicationId] as const;
  const read = useRead(key, () => referenceOpen(ids) as Promise<ReferenceOut>);
  const done = async () => { setFailure(undefined); await reload?.(); };
  const fail = (e: unknown) => { setFailure(errorText(e)); };
  const requirement = useWrite((required: boolean, idem: string) => referenceSetRequirement({ ...ids, required }, idem), [key]);
  const record = useWrite(({ status, draft }: { status: RefStatus; draft: RefDraft }, idem: string) => {
    if (status === "required") {
      // reference.set_requirement names the owner; reference.record_requested records the request
      const owner = draft.owner && draft.owner !== read.data?.requirement?.owner
        ? referenceSetRequirement({ ...ids, required: true, owner_id: draft.owner }, `${idem}-owner`)
        : Promise.resolve(null);
      return owner.then(() => referenceRecordRequested({ ...ids, provider: draft.provider ?? null, requested_at: businessDayISO(draft.requested) }, idem));
    }
    // reference.complete takes the outcome and its notes; the completed date is the call's own instant (the server
    // stamps it). REBASE: B8 S11 — a configured outcome value once reference.open serves the outcome list.
    const input = { ...ids, outcome: draft.outcome, notes: draft.notes ?? null } as ReferenceCompleteInput;
    return referenceComplete(input, idem);
  }, [key]);

  if (read.isLoading) return <ReferenceCheck status="required" owners={[]} providers={[]} outcomes={[]} loading />;
  if (read.error) {
    return isClientRefusal(read.error)
      ? <Card refused={{ reason: errorText(read.error) }}>{null}</Card>
      : <ErrorStrip message={errorText(read.error)} onRetry={() => { void read.refetch(); }} retrying={read.isFetching} />;
  }
  const reference = read.data;
  if (!reference) return null;
  const actions: ReferenceActions = {
    ...(reference.can.set_requirement !== false ? { setRequirement: (required: boolean) => requirement.mutateAsync(required).then(done, fail) } : {}),
    record: (status, draft) => record.mutateAsync({ status, draft }).then(done, fail),
  };
  return <ReferenceView reference={reference} roles={me?.roles ?? []} actions={actions} timeZone={me?.timezone} error={failure} />;
}
